import type { TranslationResult, ValidationReport } from "./api";

/**
 * Client-side download helpers. Both translation results and
 * validation reports are returned as JSON over the wire by Bridge's
 * REST API and held in the SPA's cache, so we don't need round-trip
 * server endpoints to produce a downloadable copy — we serialise on
 * the browser and trigger a Blob download.
 *
 * <p>Pattern:
 * <ol>
 *   <li>Build a {@link Blob} from the in-memory data.</li>
 *   <li>Create an object URL, click a synthetic {@code <a download>}
 *       element, revoke the URL.</li>
 * </ol>
 *
 * <p>This avoids round-tripping potentially-large IR/translation
 * payloads back to the server only to mirror them out to disk, and
 * keeps {@code BridgeController} from sprouting export endpoints
 * that would have to mirror the same serialisation logic.
 */

/**
 * Save a translation manifest as a JSON file. Filename embeds the
 * target platform and ISO date for filesystem-friendly sorting:
 * {@code bridge-translation-POWERBI_PREMIUM-2026-05-01.json}.
 */
export function downloadTranslationManifest(result: TranslationResult): void {
  const date = result.translatedAt.slice(0, 10);
  const filename = `bridge-translation-${result.target}-${date}.json`;
  // Keep the artifactJson string raw — it's already valid JSON for
  // the target platform's manifest. Re-stringifying the wrapper
  // record would double-encode it. The wrapper itself goes alongside
  // so reviewers see confidence + unmapped concepts in the same file.
  const payload = JSON.stringify(result, null, 2);
  triggerDownload(payload, filename, "application/json");
}

/**
 * Save validation findings as RFC-4180 CSV — one row per finding.
 * Columns: severity, code, location, message. Useful for piping
 * findings into ticketing pipelines or sharing with reviewers who
 * don't have access to the Bridge UI.
 */
export function downloadValidationCsv(report: ValidationReport): void {
  const date = report.generatedAt.slice(0, 10);
  const targetSlug = report.target.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const filename = `bridge-validation-${targetSlug}-${date}.csv`;
  const lines: string[] = [];
  lines.push("severity,code,location,message");
  for (const f of report.findings) {
    lines.push(
      [csvEscape(f.severity), csvEscape(f.code), csvEscape(f.location), csvEscape(f.message)].join(","),
    );
  }
  // Trailing CRLF per RFC-4180.
  triggerDownload(lines.join("\r\n") + "\r\n", filename, "text/csv");
}

function csvEscape(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "";
  let needsQuotes = false;
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    if (c === 44 /* , */ || c === 34 /* " */ || c === 13 /* \r */ || c === 10 /* \n */) {
      needsQuotes = true;
      break;
    }
  }
  if (!needsQuotes) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
}

function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
